<template>
  <div class="profile-flyout-link-list-fi">
    <div class="profile-flyout-link-list-fi__head">
      <div class="profile-flyout-link-list-fi__headline">{{ sitecoreData.LinkListHeadline }}</div>
      <a
        class="profile-flyout-link-list-fi__logout"
        :href="$fischer.pages.logout().url"
        data-test="profile-logout"
        @click="logoutUser"
      >
        <span class="fi-hidden sm:fi-inline-block">{{ $fischer.pages.logout().text }}</span>
        <icon-fi icon="log-out" />
      </a>
    </div>
    <a
      v-if="sitecoreData.PersonalPageHeadline"
      class="profile-flyout-link-list-fi__personal-page"
      :href="myfischerStartpageUrl"
    >
      <span class="fi-text-left">
        <span class="fi-block fi-semibold fi-text-size-df-18">
          {{ sitecoreData.PersonalPageHeadline }}
        </span>
        <span class="fi-mt-[2px] fi-block fi-text-size-df-14">
          {{ sitecoreData.PersonalPageSubheadline }}
        </span>
      </span>

      <icon-fi icon="arrow-right" />
    </a>

    <div class="profile-flyout-link-list-fi__link-list">
      <a
        :href="$fischer.pages.myAccount().url"
        class="profile-flyout-link-list-fi__link"
      >
        <span>{{ $fischer.pages.myAccount().text }}</span>
        <icon-fi icon="chevron-right" />
      </a>
      <a
        v-for="item in filteredWidgets"
        :key="item.Identifier"
        :href="item.Link.Url"
        class="profile-flyout-link-list-fi__link"
      >
        <span>{{ item.Link.Text }}</span>
        <span class="profile-flyout-link-list-fi__link-end">
          <count-badge-fi :count="badgeFor(item.Identifier)" />
          <icon-fi icon="chevron-right" />
        </span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CountBadgeFi, IconFi } from 'atoms';
import { computed, onMounted } from 'vue';

import { useFischer } from '@/composables/fischerPlugin';
import { badgeSourceForIdentifier, formatBadge, useUnseenCounts } from '@/composables/unseenCounts';
import { useUser } from '@/composables/user';
import type { DocumentDownload } from '@/generated/graphql';

import type { ProfileFlyoutData } from '../profile-flyout-types';

defineOptions({ name: 'ProfileFlyoutLinkListFi' });

const props = defineProps<{
  sitecoreData: ProfileFlyoutData;
  customerDocuments: DocumentDownload[] | null;
}>();

const fischer = useFischer();
const { logout } = useUser();

const myfischerStartpageUrl = computed(
  () => `${fischer.hostUrlBase()}${fischer.user.myfischerStartPageUrl()}`,
);

const filteredWidgets = computed(() => {
  let widgets = props.sitecoreData.Widgets.filter(
    (widget) =>
      !widget.RequiredGroups?.length ||
      widget.RequiredGroups.every((group) => fischer.user.groups()?.includes(group)),
  );

  if (!props.customerDocuments?.length) {
    widgets = widgets.filter((widget) => widget.Identifier !== 'Documents');
  }

  return widgets;
});

const { unseenOrders, unseenOffers, loadUnseenCounts } = useUnseenCounts();

const badgeFor = (identifier: string): string | null => {
  const source = badgeSourceForIdentifier(identifier);
  if (source === 'orders') {
    return formatBadge(unseenOrders.value);
  }
  if (source === 'offers') {
    return formatBadge(unseenOffers.value);
  }
  return null;
};

const badgedSources = computed(() =>
  filteredWidgets.value.map((widget) => badgeSourceForIdentifier(widget.Identifier)),
);

// This component only mounts once the flyout is opened, so the counts are
// fetched on first open rather than on every page load. Each count is requested
// only when its row survived the permission filter above: the backend rejects a
// query the user lacks the privilege for, which is a guaranteed console.error
// and a wasted round trip.
onMounted(() => {
  loadUnseenCounts({
    orders: badgedSources.value.includes('orders'),
    offers: badgedSources.value.includes('offers'),
  });
});

const logoutUser = async (evt: Event) => {
  evt?.preventDefault?.();
  (evt as any)?.stopImmediatePropagation?.();
  await logout();
};
</script>

<style scoped src="./profile-flyout-link-list-fi" />
